import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "@env";
import {
  PriceTableDTO,
  SavePriceTableDTO,
  SearchPriceTableParams,
  PriceTableListResponse,
  PriceTableInfo,
  ProductDTO,
  UpdatePriceTableDTO
} from "../../modules/private/modules/price-table/models/price-table-dto";
import { PageManager } from "@shared/models/page-manager";

@Injectable({
  providedIn: "root"
})
export class PriceTableService {
  private priceTableUrl = "price-tables-management/";
  private priceTableUrlv2 = "price-table/";

  constructor(private http: HttpClient) {}

  /**
   * Cria uma nova tabela de preço.
   * POST price-tables-management/create
   */
  save(dto: SavePriceTableDTO): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${environment.apiUrl}${this.priceTableUrl}create`,
      dto
    );
  }

  /**
   * Exporta uma tabela de preço em formato Excel (.xlsx).
   * GET price-tables-management/price-table/{id}/export
   */
  exportPriceTable(id: number): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}${this.priceTableUrlv2}${id}/export`,
      {
        responseType: "blob"
      }
    );
  }

  downloadTemplate(priceTableId: number): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}${this.priceTableUrlv2}descriptions/${priceTableId}/template`,
      {
        responseType: "blob"
      }
    );
  }
  /**
   * Atualiza uma tabela de preço existente.
   * PUT price-tables-management/price-tables/:id
   */
  update(
    id: number,
    dto: UpdatePriceTableDTO
  ): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(
      `${environment.apiUrl}${this.priceTableUrl}${id}`,
      dto
    );
  }

  /**
   * Busca as descrições da tabela de preço por ID com paginação e localeId.
   * GET price-tables-management/:id/descriptions
   */
  getDescriptionsByPriceTableId(
    id: number,
    filters: {
      pageIndex: number;
      pageSize: number;
      sort?: string;
      name?: string;
      item?: string;
      model?: string;
    }
  ): Observable<any> {
    let params = new HttpParams()
      .set("page", filters.pageIndex.toString())
      .set("size", filters.pageSize.toString());

    if (filters.sort) {
      params = params.set("sort", filters.sort);
    }
    if (filters.name) {
      params = params.set("name", filters.name);
    }
    if (filters.item) {
      params = params.set("item", filters.item);
    }
    if (filters.model) {
      params = params.set("model", filters.model);
    }

    return this.http.get<any>(
      `${environment.apiUrl}${this.priceTableUrlv2}${id}/descriptions`,
      { params }
    );
  }

  /**
   * Lista tabelas de preço com filtros, paginação e ordenação.
   * GET price-tables-management/prices-tables
   */
  search(
    filters: SearchPriceTableParams,
    pageManager: PageManager,
    sort: string
  ): Observable<PriceTableListResponse> {
    let params = new HttpParams()
      .set("page", pageManager.pageIndex.toString())
      .set("size", pageManager.pageSize.toString());

    if (sort) params = params.append("sort", sort);
    if (filters.name) params = params.set("name", filters.name);
    if (filters.description)
      params = params.set("description", filters.description);
    if (filters.products?.length) {
      filters.products.forEach((productId) => {
        params = params.append("products", productId.toString());
      });
    }

    return this.http.get<PriceTableListResponse>(
      `${environment.apiUrl}${this.priceTableUrl}prices-tables`,
      { params }
    );
  }

  /**
   * Importa descritivos de uma tabela de preço a partir de um arquivo Excel.
   * POST price-tables-management/import
   */
  uploadDescriptions(
    file: File,
    tableId: number
  ): Observable<{
    status: string;
    message: string;
    errors: { field: string; line: number; message: string }[];
    validItems: any[];
  }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("tableId", tableId.toString());

    return this.http.post<{
      status: string;
      message: string;
      errors: { field: string; line: number; message: string }[];
      validItems: any[];
    }>(`${environment.apiUrl}${this.priceTableUrl}import`, formData);
  }

  uploadToMultipleTables(
    file: File,
    tableIds: number[]
  ): Observable<{
    status: string;
    message: string;
    errors: { tableId: number; field: string; line: number; message: string }[];
    validItems: any[];
  }> {
    const formData = new FormData();
    formData.append("file", file);

    const params = tableIds.map((id) => `validTableIds=${id}`).join("&");

    const url = `${environment.apiUrl}${this.priceTableUrl}import/bulk?${params}`;

    return this.http.post<{
      status: string;
      message: string;
      errors: {
        tableId: number;
        field: string;
        line: number;
        message: string;
      }[];
      validItems: any[];
    }>(url, formData);
  }

  deleteDescriptionById(descriptiveId: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}${this.priceTableUrlv2}descriptions/${descriptiveId}`
    );
  }

  /**
   * Busca uma tabela de preço por ID.
   * GET price-tables-management/:id
   */

  getPriceTableById(id: number): Observable<{ content: PriceTableInfo[] }> {
    return this.http.get<{ content: PriceTableInfo[] }>(
      `${environment.apiUrl}${this.priceTableUrlv2}table/${id}`
    );
  }

  getAllProducts(): Observable<ProductDTO[]> {
    return this.http.get<ProductDTO[]>(
      `${environment.apiUrl}${this.priceTableUrl}products`
    );
  }

  getModels(): Observable<{ id: number; model: string }[]> {
    return this.http.get<{ id: number; model: string }[]>(
      `${environment.apiUrl}${this.priceTableUrlv2}models`
    );
  }
}
